// 내 곡 추가/수정 다이얼로그 (메트로놈 프리셋 저장 · 연습 일지 곡 관리 공용)
import React, { useEffect, useState } from 'react';
import {
  Button, DialogActions, DialogContent, DialogTitle, FormControlLabel, Switch, TextField, Typography,
} from '@mui/material';
import Dialog from '../components/AppDialog';
import { useDialog } from '../contexts/DialogProvider';
import { useMetronome } from './MetronomeProvider';

// piece가 있으면 수정, 없으면 새 곡. preset은 새 곡에 담을 메트로놈 설정(configToPiece 결과)
export default function PieceDialog({ open, piece, preset, onClose, onSaved }) {
  const { createPiece, updatePiece, removePiece } = useMetronome();
  const { toast, confirm } = useDialog();
  const [title, setTitle] = useState('');
  const [useTarget, setUseTarget] = useState(false);
  const [target, setTarget] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(piece?.title || '');
    setUseTarget(!!piece?.target_bpm);
    setTarget(piece?.target_bpm ? String(piece.target_bpm) : '');
    setError('');
  }, [open, piece]);

  const submit = async () => {
    const name = title.trim();
    if (!name) {
      setError('곡 이름을 입력해 주세요.');
      return;
    }
    const targetBpm = useTarget ? Number(target) : null;
    if (useTarget && !(targetBpm >= 20 && targetBpm <= 300)) {
      setError('목표 템포는 20~300 사이로 입력해 주세요.');
      return;
    }
    setSaving(true);
    try {
      const data = { title: name, target_bpm: targetBpm };
      const saved = piece ? await updatePiece(piece.id, data) : await createPiece({ ...preset, ...data });
      onSaved?.(saved);
      onClose();
    } catch (e) {
      setError(e.response?.data?.message || '저장하지 못했어요.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    const ok = await confirm({
      title: '곡 삭제',
      message: `"${piece.title}"을(를) 내 곡에서 지울까요? 지난 연습 기록은 그대로 남아요.`,
      confirmText: '삭제',
      color: 'error',
    });
    if (!ok) return;
    try {
      await removePiece(piece.id);
      onClose();
    } catch (e) {
      toast('삭제하지 못했어요.', 'error');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{piece ? '곡 수정' : '내 곡 추가'}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="곡 이름"
          placeholder="쇼팽 녹턴 Op.9-2"
          value={title}
          onChange={(e) => { setTitle(e.target.value); setError(''); }}
          inputProps={{ maxLength: 100 }}
          sx={{ mt: 1 }}
        />
        <FormControlLabel
          sx={{ mt: 1.5 }}
          control={<Switch color="secondary" checked={useTarget} onChange={(e) => { setUseTarget(e.target.checked); setError(''); }} />}
          label="목표 템포 정하기"
        />
        {useTarget && (
          <TextField
            fullWidth
            label="목표 템포 (BPM)"
            type="number"
            value={target}
            onChange={(e) => { setTarget(e.target.value); setError(''); }}
            inputProps={{ min: 20, max: 300, inputMode: 'numeric' }}
            sx={{ mt: 1 }}
          />
        )}
        {!piece && preset && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            지금 메트로놈 설정(♩={preset.bpm}, {preset.beats}/{preset.beat_unit}{preset.grouping ? ` · ${preset.grouping}` : ''})이 이 곡의 프리셋으로 저장돼요.
          </Typography>
        )}
        {error && <Typography variant="body2" color="error" sx={{ mt: 1 }}>{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {piece && <Button color="error" onClick={remove} sx={{ mr: 'auto' }}>삭제</Button>}
        <Button color="inherit" onClick={onClose}>취소</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>저장</Button>
      </DialogActions>
    </Dialog>
  );
}
